import NotificationModule from "./notification_module";
class EllipseGradingModule {
  constructor(config) {
    this.controller = config.controller;
    this.iolGradingApi = config.iolGradingApi;
    this.saveUrl = config.saveUrl;
    this.method = config.method;
    this.minPoints = config.minPoints || 10;
    this.iolDiameter = config.iolDiameter;
    this.ellipseData = config.ellipseData || null;
    this.cornerstone = config.cornerstone; // Store cornerstone dependency
    this.ellipseRenderer = null;
    this.visibilityStates = config.visibilityStates;
    this.activeTypes = config.activeTypes;
    const loaderElem = document.getElementById("loader"); // or any selector
    this.notifications = new NotificationModule(loaderElem);
    this.gradingEllipseData = config.gradingEllipseData || null;
    this.users = config.users;
  }

  designEllipse(annotations, imageUrl = null) {
    return new Promise((resolve, reject) => {
      // Validate imageUrl
      if (!imageUrl || typeof imageUrl !== "string" || imageUrl.trim() === "") {
        alert("Invalid image URL provided.");
        reject(new Error("Invalid image URL"));
        return;
      }

      // Get the annotation for this imageUrl

      const imageAnn = annotations[imageUrl];
      if (!imageAnn) {
        alert("No annotation data found for this image URL.");
        reject(new Error("Annotation not found for image URL"));
        return;
      }

      // Optional: preload the image
      const img = new Image();
      img.onload = () => {
        const result = {
          IOL: [],
          Pupil: [],
          Cornea: [],
          IOL_diameter: this.iolDiameter,
        };

        if (imageAnn?.IOLProbe?.data) {
          imageAnn.IOLProbe.data.forEach((m) => {
            if (m.handles?.end) {
              result.IOL.push([
                Math.round(m.handles.end.x),
                Math.round(m.handles.end.y),
              ]);
            }
          });
        }

        if (imageAnn?.PupilProbe?.data) {
          imageAnn.PupilProbe.data.forEach((m) => {
            if (m.handles?.end) {
              result.Pupil.push([
                Math.round(m.handles.end.x),
                Math.round(m.handles.end.y),
              ]);
            }
          });
        }

        if (imageAnn?.CorneaProbe?.data) {
          imageAnn.CorneaProbe.data.forEach((m) => {
            if (m.handles?.end) {
              result.Cornea.push([
                Math.round(m.handles.end.x),
                Math.round(m.handles.end.y),
              ]);
            }
          });
        }

        const insufficient = [];
        if (result.IOL.length < this.minPoints) insufficient.push("IOL");
        if (result.Pupil.length < this.minPoints) insufficient.push("Pupil");
        if (result.Cornea.length < this.minPoints) insufficient.push("Cornea");

        if (insufficient.length > 0) {
          alert(
            `Please add at least ${
              this.minPoints
            } points for: ${insufficient.join(", ")}`
          );
          this.notifications.hideLoader();
          // reject(new Error("Insufficient probe points"));
          return;
        }

        this.getIolDecentration(result).then(resolve).catch(reject);
      };

      img.onerror = () => {
        alert("Failed to load the image from the provided URL.");
        reject(new Error("Image load error"));
      };

      // Start loading the image
      img.src = imageUrl;
    });
  }

  renderEllipses(event) {
    const hasEllipseData =
      this.ellipseData && Object.keys(this.ellipseData).length > 0;
    const hasGradingData =
      this.gradingEllipseData &&
      Object.keys(this.gradingEllipseData).length > 0 &&
      this.users.value !== "adjudicator";

    // If no valid data to use, return early
    if (!hasEllipseData && !hasGradingData) return;

    // Decide which data to use
    const ellipseDataToUse = hasEllipseData
      ? this.ellipseData
      : this.gradingEllipseData[this.controller.usersTarget.value];
    // Get current image URL
    const currentUrl = this.controller.url;
    // Get ellipse data specific to this image
    const currentImageData = ellipseDataToUse[currentUrl];

    // If no data for this image, don't draw anything
    if (!currentImageData || currentImageData.length === 0) return;

    const { element, canvasContext: context, enabledElement } = event.detail;
    context.save();
    this.cornerstone.setToPixelCoordinateSystem(enabledElement, context);

    // Draw using the data for current image
    this.drawAllEllipses(context, currentImageData[0]); // Assuming latest result

    context.restore();
  }

  drawAllEllipses(context, data) {
    const ellipseConfig = {
      Cornea: { color: "#FF0000", label: "Cornea" },
      IOL: { color: "#00FF00", label: "IOL" },
      Pupil: { color: "#0000FF", label: "Pupil" },
    };
    const scaleFactor = 2;
    const centers = {};

    Object.entries(ellipseConfig).forEach(([key, { color, label }]) => {
      const params = data[key]?.params;
      if (!params?.center_x || !params?.center_y) return;

      const cx = params.center_x;
      const cy = params.center_y;
      const rx = (params.major_axis / 2) * scaleFactor;
      const ry = (params.minor_axis / 2) * scaleFactor;
      const rotation = ((params.major_axis_angle || 0) * Math.PI) / 180;

      centers[key] = [cx, cy];
      const visibilityState = this.visibilityStates[key.toLowerCase()];

      if (visibilityState === "contour" || visibilityState === "both") {
        // Draw ellipse contour
        context.setLineDash([]);
        context.beginPath();
        context.ellipse(cx, cy, rx, ry, rotation, 0, 2 * Math.PI);
        context.strokeStyle = color;
        context.lineWidth = 3;
        context.stroke();

        // Draw axis lines (major and minor)
        const axis = data[key]?.axis_endpoints;
        if (axis) {
          // Major axis
          if (axis.major_start && axis.major_end) {
            context.beginPath();
            context.moveTo(axis.major_start[0], axis.major_start[1]);
            context.lineTo(axis.major_end[0], axis.major_end[1]);
            context.strokeStyle = color;
            context.lineWidth = 1;
            context.setLineDash([2, 2]);
            context.stroke();
          }

          // Minor axis
          if (axis.minor_start && axis.minor_end) {
            context.beginPath();
            context.moveTo(axis.minor_start[0], axis.minor_start[1]);
            context.lineTo(axis.minor_end[0], axis.minor_end[1]);
            context.strokeStyle = color;
            context.lineWidth = 1;
            context.setLineDash([2, 2]);
            context.stroke();
          }
        }

        // Always draw center point
        context.setLineDash([]);
        context.beginPath();
        context.arc(cx, cy, 3, 0, 2 * Math.PI);
        context.strokeStyle = color;
        context.lineWidth = 1;
        context.stroke();

        // Always draw label
        context.font = "bold 14px Arial";
        context.textAlign = "center";
        context.fillStyle = color;
        context.fillText(label, cx, cy - ry - 5);
      }
    });

    //  Draw dotted line from IOL to Pupil only if both are visible
    const iolVisible = ["contour", "both"].includes(this.visibilityStates.iol);
    const pupilVisible = ["contour", "both"].includes(
      this.visibilityStates.pupil
    );

    if (iolVisible && pupilVisible && centers.IOL && centers.Pupil) {
      const [iolX, iolY] = centers.IOL;
      const [pupilX, pupilY] = centers.Pupil;

      context.beginPath();
      context.moveTo(iolX, iolY);
      context.lineTo(pupilX, pupilY);
      context.strokeStyle = "#17A2B8";
      context.lineWidth = 1;
      context.setLineDash([10, 2, 2, 2]);
      context.stroke();

      const distance =
        data.IOL_Pupil_Distance != null ? data.IOL_Pupil_Distance : "N/A";

      if (
        ["contour", "both"].includes(this.visibilityStates.cornea) &&
        centers.Cornea
      ) {
        const [corneaX, corneaY] = centers.Cornea;

        context.font = "bold 36px Arial";
        context.fillStyle = "green";
        context.textAlign = "right";
        context.setLineDash([]);

        context.fillText(`${distance} mm`, corneaX - 500, corneaY);
      }
    }
  }

  async getIolDecentration(data) {
    try {
      const response = await fetch(`${this.iolGradingApi}/IOL-Decentration`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document
            .querySelector('meta[name="csrf-token"]')
            ?.getAttribute("content"),
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API Error: ${response.status} - ${errorText}`);
      }

      const result = await response.json();

      // Initialize ellipseData if not already
      this.ellipseData = this.ellipseData || {};
      // Get the current image URL
      const currentUrl = this.controller.url;

      // Always replace data for the same URL, or create new entry for different URLs
      this.ellipseData[currentUrl] = [result];

      this.addEllipseRenderer();

      // Return only the current URL's data
      return this.ellipseData;
    } catch (error) {
      console.error("IOL Decentration API Error:", error);
      // Consider returning null or throwing the error depending on your error handling strategy
      return null;
    }
  }

  addEllipseRenderer() {
    const elements = this.cornerstone.getEnabledElements();
    if (!elements.length) return;

    this.ellipseRenderer =
      this.ellipseRenderer || this.renderEllipses.bind(this);

    elements.forEach(({ element }) => {
      element.removeEventListener(
        "cornerstoneimagerendered",
        this.ellipseRenderer
      );
      element.addEventListener(
        "cornerstoneimagerendered",
        this.ellipseRenderer
      );

      if (this.cornerstone.getImage(element)) {
        this.cornerstone.updateImage(element);
      }
    });
  }

  setVisibility(type) {
    type = type.toLowerCase();
    // Validate that it's a recognized type
    if (!["iol", "pupil", "cornea"].includes(type)) {
      console.error(`Invalid ellipse type: ${type}`);
      return;
    }
    // Cycle through visibility states: both -> contour -> points -> none -> both
    const states = ["both", "contour", "points", "none"];
    const currentIndex = states.indexOf(this.visibilityStates[type]);
    const nextIndex = (currentIndex + 1) % states.length;
    this.visibilityStates[type] = states[nextIndex];
    // Re-render with the new visibility state
    this.addEllipseRenderer();

    return this.visibilityStates[type];
  }
}

// Export the class for use in other modules
export default EllipseGradingModule;
